import { useCallback } from "react";
import useSWR from "swr";
import { useAuthentication } from "./useAuthentication";
import {
  fetchTrackingParents,
  createTrackingParent,
  updateTrackingParent,
  deleteTrackingParent,
  type TrackingParent,
  type ParentCreatePayload,
  type ParentUpdatePayload,
} from "../services/tracking/parentsService";

const KEY = "tracking-parents";

export function useTrackingParents() {
  const { user } = useAuthentication();
  const token = user?.auth?.access_token;

  const { data, error, isLoading, mutate } = useSWR<TrackingParent[]>(
    token ? [KEY, token] : null,
    () => fetchTrackingParents(token),
    { revalidateOnFocus: false }
  );

  const refetch = useCallback(() => mutate(), [mutate]);
  const list = Array.isArray(data) ? data : [];

  const create = useCallback(
    async (payload: ParentCreatePayload) => {
      const created = await createTrackingParent(token, payload);
      await mutate();
      return created;
    },
    [token, mutate]
  );

  const update = useCallback(
    async (id: string, payload: ParentUpdatePayload) => {
      const updated = await updateTrackingParent(token, id, payload);
      await mutate();
      return updated;
    },
    [token, mutate]
  );

  const remove = useCallback(
    async (id: string) => {
      await deleteTrackingParent(token, id);
      await mutate();
    },
    [token, mutate]
  );

  return {
    parents: list,
    isLoading,
    error,
    refetch,
    create,
    update,
    remove,
    accessToken: token,
  };
}

export type { TrackingParent, ParentCreatePayload, ParentUpdatePayload };
